import React, { useEffect, useState } from 'react';
import api from '../../api/client';
import { useNavigate } from 'react-router-dom';
import { Loader2, Users, Award, Clock, TrendingUp, DollarSign } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

interface RecruiterMetric {
  recruiterId: string;
  name: string;
  email?: string;
  totalVacancies: number;
  closedVacancies: number;
  totalFee: number;
  avgTimeToFill: number;
  candidatesProcessed: number;
}

interface TimeAnalysisItem {
  recruiterId: string;
  name: string;
  avgCycleDays: number;
  count: number;
}

const RecruiterPerformanceDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState<RecruiterMetric[]>([]);
  const [timeAnalysis, setTimeAnalysis] = useState<TimeAnalysisItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [metricsRes, timeRes] = await Promise.all([
          api.get('/recruiter-performance/metrics'),
          api.get('/recruiter-performance/time-analysis')
        ]);
        setMetrics(metricsRes.data);
        setTimeAnalysis(timeRes.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', color: '#64748b' }}>
        <Loader2 size={40} style={{ animation: 'spin 1s linear infinite' }} />
        <style>{`@keyframes spin { 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  const totalRecruiters = metrics.length;
  const totalClosed = metrics.reduce((sum, m) => sum + m.closedVacancies, 0);
  const totalFee = metrics.reduce((sum, m) => sum + m.totalFee, 0);
  const avgTime = timeAnalysis.length > 0 ? Math.round(timeAnalysis.reduce((sum, t) => sum + t.avgCycleDays, 0) / timeAnalysis.length) : 0;

  return (
    <div style={{ padding: '0 20px 40px 20px', fontFamily: 'Inter, sans-serif' }}>
      
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <div style={{ width: 48, height: 48, borderRadius: 14, background: 'linear-gradient(135deg, #6a11cb, #2575fc)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', boxShadow: '0 8px 20px rgba(106,17,203,0.3)' }}>
          <Users size={24} />
        </div>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 800, color: '#1a1a2e', margin: 0 }}>Эффективность рекрутеров</h1>
          <p style={{ color: '#64748b', margin: '4px 0 0 0', fontSize: 14 }}>Сравнительный анализ работы команды подбора</p>
        </div>
      </div>

      {/* KPI Карточки */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        <div style={styles.kpiCard}>
          <div style={{ ...styles.kpiIcon, background: 'rgba(37,117,252,0.1)', color: '#2575fc' }}><Users size={22} /></div>
          <div><div style={styles.kpiLabel}>Рекрутеров</div><div style={styles.kpiValue}>{totalRecruiters}</div></div>
        </div>
        <div style={styles.kpiCard}>
          <div style={{ ...styles.kpiIcon, background: 'rgba(16,185,129,0.1)', color: '#10b981' }}><Award size={22} /></div>
          <div><div style={styles.kpiLabel}>Закрыто вакансий</div><div style={styles.kpiValue}>{totalClosed}</div></div>
        </div>
        <div style={styles.kpiCard}>
          <div style={{ ...styles.kpiIcon, background: 'rgba(106,17,203,0.1)', color: '#6a11cb' }}><DollarSign size={22} /></div>
          <div><div style={styles.kpiLabel}>Общий гонорар</div><div style={styles.kpiValue}>{totalFee.toLocaleString()} ₽</div></div>
        </div>
        <div style={styles.kpiCard}>
          <div style={{ ...styles.kpiIcon, background: 'rgba(245,158,11,0.1)', color: '#f59e0b' }}><Clock size={22} /></div>
          <div><div style={styles.kpiLabel}>Ср. время цикла</div><div style={styles.kpiValue}>{avgTime} дн.</div></div>
        </div>
      </div>

      {/* График */}
      <div style={{ ...styles.glassCard, marginBottom: 24 }}>
        <h3 style={styles.sectionTitle}><TrendingUp size={18} /> Среднее время цикла (дни)</h3>
        <div style={{ height: 260 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={timeAnalysis} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="perfGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#6a11cb" />
                  <stop offset="100%" stopColor="#2575fc" />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#64748b' }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: '#64748b' }} />
              <Tooltip contentStyle={{ background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(10px)', borderRadius: 12, border: '1px solid rgba(255,255,255,0.5)' }} />
              <Bar dataKey="avgCycleDays" fill="url(#perfGradient)" radius={[8, 8, 0, 0]} animationDuration={1000} name="Среднее время цикла" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Таблица */}
      <div style={styles.glassCard}>
        <h3 style={styles.sectionTitle}><Award size={18} /> Сравнительная таблица</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={styles.th}>Рекрутер</th>
                <th style={styles.th}>Вакансий (всего)</th>
                <th style={styles.th}>Закрыто</th>
                <th style={styles.th}>Гонорар</th>
                <th style={styles.th}>Сред. время</th>
                <th style={styles.th}>Кандидатов</th>
              </tr>
            </thead>
            <tbody>
              {metrics.map(m => (
                <tr
                  key={m.recruiterId}
                  onClick={() => navigate(`/app/recruitment-analytics/performance/${m.recruiterId}`)}
                  style={{ cursor: 'pointer', transition: 'background 0.2s' }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(37,117,252,0.03)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <td style={styles.td}><strong>{m.name}</strong></td>
                  <td style={styles.td}>{m.totalVacancies}</td>
                  <td style={styles.td}><span style={{ color: '#10b981', fontWeight: 600 }}>{m.closedVacancies}</span></td>
                  <td style={styles.td}>{m.totalFee.toLocaleString()} ₽</td>
                  <td style={styles.td}>{m.avgTimeToFill || '—'} дн.</td>
                  <td style={styles.td}>{m.candidatesProcessed}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  glassCard: { background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderRadius: 24, padding: 24, border: '1px solid rgba(255,255,255,0.6)', boxShadow: '0 12px 40px rgba(31,38,135,0.06)' },
  kpiCard: { background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(20px)', borderRadius: 20, padding: 20, border: '1px solid rgba(255,255,255,0.6)', boxShadow: '0 8px 24px rgba(31,38,135,0.04)', display: 'flex', alignItems: 'center', gap: 16 },
  kpiIcon: { width: 48, height: 48, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  kpiLabel: { fontSize: 13, color: '#64748b', fontWeight: 600, marginBottom: 4 },
  kpiValue: { fontSize: 24, fontWeight: 800, color: '#1a1a2e' },
  sectionTitle: { fontSize: 18, fontWeight: 700, color: '#1a1a2e', margin: '0 0 20px 0', display: 'flex', alignItems: 'center', gap: 8 },
  th: { textAlign: 'left', padding: '12px 16px', borderBottom: '1px solid rgba(0,0,0,0.05)', color: '#64748b', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 },
  td: { padding: '14px 16px', borderBottom: '1px solid rgba(0,0,0,0.03)', fontSize: 14, color: '#1a1a2e' },
};

export default RecruiterPerformanceDashboard;
